@use "ui/styles/colors";
@use "ui/styles/sizes";
@use "ui/styles/effects";
@use "ui/styles/fonts";
@use "ui/styles/zindex";

// Radix `hideWhenDetached` parity: Base UI marks the positioner with [data-anchor-hidden]
// when the anchor is scrolled out of view; hide the popup (and its arrow) in that state.
.hideWhenDetached[data-anchor-hidden] {
    visibility: hidden;
}

// Stacking lives on the Positioner: Base UI applies `position: fixed` here, so this is the
// element z-index applies to (the Popup below it is a static child, where z-index is ignored).
//
// Only this rule declares `z-index`. A call site that needs a different layer sets
// `--popover-z-index` through `positionerClassName` instead of redeclaring `z-index`: both
// selectors are a single class, so competing `z-index` declarations tie on specificity and the
// winner comes down to CSS-module insertion order, which neither side controls.
.positioner {
    z-index: var(--popover-z-index, #{zindex.$dropdown});
}

.content {
    // Generic alias for the measured trigger width, so consumers don't couple to the library's var
    // name. Base UI sets --anchor-width on the positioner, which this popup inherits from.
    --popover-trigger-width: var(--anchor-width);
    background: var(--background-color, colors.$white);
    border-radius: effects.$borderRadiusS;
    overflow: visible;
    box-shadow: 0px 4px 8px 0px effects.$shadowColor;
    border: 1px solid var(--border-color, colors.$gray20);
    // Enter/exit animation driven by Base UI's [data-starting-style]/[data-ending-style] attrs.
    transition-property: opacity, transform;
    transition-duration: 300ms;
    transition-timing-function: cubic-bezier(0.16, 1, 0.3, 1);

    &[data-starting-style],
    &[data-ending-style] {
        opacity: 0;
    }

    &[data-side="top"] {
        transform-origin: bottom center;
    }

    &[data-side="bottom"] {
        transform-origin: top center;
    }

    &[data-side="left"] {
        transform-origin: right center;
    }

    &[data-side="right"] {
        transform-origin: left center;
    }

    &[data-side="top"][data-starting-style],
    &[data-side="top"][data-ending-style] {
        transform: translateY(2px);
    }

    &[data-side="bottom"][data-starting-style],
    &[data-side="bottom"][data-ending-style] {
        transform: translateY(-2px);
    }

    &[data-side="left"][data-starting-style],
    &[data-side="left"][data-ending-style] {
        transform: translateX(2px);
    }

    &[data-side="right"][data-starting-style],
    &[data-side="right"][data-ending-style] {
        transform: translateX(-2px);
    }
}

// Base UI positions only the arrow's cross-axis (inline `left`/`top`); the static side must be
// anchored in CSS so the arrow sits on the popup's edge pointing at the trigger (Radix did this
// internally). Insets follow the SVG geometry (10x5; on left/right the rotated SVG's visual width
// is 5px centered in the 10px box), pulled in 1px so the arrow's base overlaps the popup's 1px
// border and covers it — otherwise the border line cuts across the junction.
.arrow {
    display: flex;

    &[data-side="top"] {
        bottom: -4px;
    }

    &[data-side="bottom"] {
        top: -4px;
    }

    &[data-side="left"] {
        right: -6.5px;
    }

    &[data-side="right"] {
        left: -6.5px;
    }
}

.arrowSvg {
    display: block;
    fill: var(--background-color, colors.$white);
    filter: drop-shadow(0px -1px 2px rgba(0, 0, 0, 0.08));
}

// Matches the popup's border so the arrow reads as part of its outline.
.arrowStroke {
    stroke: var(--border-color, colors.$gray20);
    stroke-width: 1px;
}

.arrow[data-side="top"] .arrowSvg {
    transform: rotate(180deg);
}

.arrow[data-side="left"] .arrowSvg {
    transform: rotate(90deg);
}

.arrow[data-side="right"] .arrowSvg {
    transform: rotate(-90deg);
}

.close {
    @include effects.iconButton;
}
